import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { v4 as uuidv4 } from "uuid";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Paper,
  Stack,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import api from "../../utils/api";

const AddWorkOrder = ({
  open,
  handleClose,
  propertyId,
  onWorkOrderAdded,
  clubMembershipTypeCode = "",
}) => {
  const [loggedInUserId, setLoggedInUserId] = useState(null);
  const [workOrderTypes, setWorkOrderTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isReloading, setIsReloading] = useState(false);

  const [formData, setFormData] = useState({
    description: "",
    details: "",
    property_id: propertyId || "",
    workorder_type_id: "",
    workorder_subtype_id: "",
    location_id: "",
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const submittingRef = useRef(false);
  const [requestId, setRequestId] = useState(uuidv4());

  const dialogCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 18px 60px rgba(0,0,0,0.14)",
    backgroundColor: "rgba(255,255,255,0.98)",
  };

  const sectionCardSx = {
    p: 2.5,
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    backgroundColor: "rgba(255,255,255,0.98)",
    boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 700,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "0 8px 20px rgba(47,99,173,0.22)",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 700,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");

    if (!storedUser) {
      setLoggedInUserId(null);
      return;
    }

    try {
      const parsedUser = JSON.parse(storedUser);
      setLoggedInUserId(parsedUser?.userId ?? null);
    } catch (error) {
      console.error("Failed to parse logged in user from sessionStorage", error);
      setLoggedInUserId(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadTypes = async () => {
      try {
        const url = propertyId
          ? `/workorder-types/by-property?property_id=${encodeURIComponent(
              propertyId
            )}`
          : `/workorder-types`;

        const res = await api.get(url);
        if (!cancelled) setWorkOrderTypes(res.data?.types || res.data || []);
      } catch (err) {
        console.error("Failed to fetch work order types:", err);
        if (!cancelled) setWorkOrderTypes([]);
      }
    };

    loadTypes();

    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      property_id: propertyId || "",
    }));
  }, [propertyId]);

  useEffect(() => {
    if (!formData.workorder_type_id) {
      setSubtypes([]);
      setLocations([]);
      return;
    }

    const fetchData = async () => {
      try {
        const [subRes, locRes] = await Promise.all([
          api.get(
            `/workorder-subtypes-by-type?type_id=${formData.workorder_type_id}`
          ),
          api.get(
            `/workorder-locations-by-type?type_id=${formData.workorder_type_id}`
          ),
        ]);
        setSubtypes(subRes.data || []);
        setLocations(locRes.data || []);
      } catch (err) {
        console.error("Failed to fetch subtypes/locations:", err);
        setSubtypes([]);
        setLocations([]);
      }
    };

    fetchData();
  }, [formData.workorder_type_id]);

  const handleFormChange = (e) => {
    const { name } = e.target;
    const rawValue = e.target.value;

    const value = name.endsWith("_id")
      ? rawValue === ""
        ? ""
        : Number(rawValue)
      : rawValue;

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === "workorder_type_id") {
        updated.workorder_subtype_id = "";
        updated.location_id = "";
        updated.description = "";
      }

      if (name === "workorder_subtype_id") {
        const selectedSubtype = subtypes.find(
          (s) => Number(s.id) === Number(value)
        );
        updated.description = selectedSubtype
          ? selectedSubtype.subtype_name
          : "";
      }

      return updated;
    });
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (!acceptedFiles?.length) return;

    setSelectedFiles((prev) => {
      const combined = [...prev, ...acceptedFiles];

      const uniqueFiles = combined.filter((file, index, self) => {
        return (
          index ===
          self.findIndex(
            (f) =>
              f.name === file.name &&
              f.size === file.size &&
              f.lastModified === file.lastModified
          )
        );
      });

      return uniqueFiles;
    });
  }, []);

  const removeFile = (indexToRemove) => {
    setSelectedFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
        ".docx",
      ],
    },
    multiple: true,
  });

  const resetForm = () => {
    setFormData({
      description: "",
      details: "",
      property_id: propertyId || "",
      workorder_type_id: "",
      workorder_subtype_id: "",
      location_id: "",
    });
    setSelectedFiles([]);
    setSubtypes([]);
    setLocations([]);
    setRequestId(uuidv4());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      if (!loggedInUserId) {
        setSnackbarMessage("Unable to identify the logged-in user.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      let {
        description,
        details,
        workorder_type_id,
        workorder_subtype_id,
        location_id,
      } = formData;

      const propertyIdEffective = (propertyId ?? formData.property_id ?? "")
        .toString()
        .trim();

      const toNum = (v) => (v === "" || v == null ? "" : Number(v));
      workorder_type_id = toNum(workorder_type_id);
      workorder_subtype_id = toNum(workorder_subtype_id);
      location_id = toNum(location_id);

      if ((!description || !description.trim()) && workorder_subtype_id) {
        const sel = subtypes.find(
          (s) => Number(s.id) === Number(workorder_subtype_id)
        );
        if (sel?.subtype_name) description = sel.subtype_name;
      }

      const descTrim = (description || "").trim();
      const detailsTrim = (details || "").trim();

      const missing = [];
      if (!descTrim) missing.push("description");
      if (!detailsTrim) missing.push("details");
      if (!propertyIdEffective) missing.push("property_id");
      if (!workorder_type_id) missing.push("workorder_type_id");
      if (!workorder_subtype_id) missing.push("workorder_subtype_id");
      if (!location_id) missing.push("location_id");

      if (missing.length) {
        setSnackbarMessage(
          `Please fill in all required fields: ${missing.join(", ")}`
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }

      const payload = {
        description: descTrim,
        details: detailsTrim,
        property_id: propertyIdEffective,
        workorder_type_id,
        workorder_subtype_id,
        location_id,
        request_id: requestId,
        opened_by_user_id: loggedInUserId,
      };

      const res = await api.post(`/addWorkOrder`, payload);
      const workorder_id = res.data?.workOrderId;

      if (!workorder_id) {
        throw new Error("Missing workOrderId from response");
      }

      if (selectedFiles.length > 0) {
        const fileData = new FormData();
        fileData.append("workorder_id", workorder_id);
        fileData.append("uploaded_by", loggedInUserId);

        selectedFiles.forEach((file) => {
          fileData.append("upload_urls", file);
        });

        try {
          await api.post("/workorder-files", fileData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          console.error("Error uploading work order files:", err);
          setSnackbarMessage(
            err.response?.data?.message ||
              "File upload failed. Please try again."
          );
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      }

      setSnackbarMessage("Work order created successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      resetForm();
      onWorkOrderAdded?.();
      setConfirmOpen(true);
    } catch (err) {
      console.error("Error creating work order:", err);
      setSnackbarMessage(
        err.response?.data?.message || "Failed to create work order."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={isSubmitting ? undefined : handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogCardSx }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle
            sx={{
              px: { xs: 2.5, md: 3 },
              pt: 3,
              pb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#223142",
                mb: 0.5,
              }}
            >
              Create New Work Order
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Submit a maintenance request for this property.
            </Typography>
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              px: { xs: 2, md: 3 },
              py: 3,
              backgroundColor: "rgba(47,99,173,0.015)",
            }}
          >
            <Stack spacing={2.5}>
              <Paper elevation={0} sx={sectionCardSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <BuildOutlinedIcon sx={{ color: "#2f63ad" }} />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: "#223142" }}
                  >
                    Work Order Information
                  </Typography>
                </Box>

                <Stack spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel id="workorder-type-label">
                      Work Order Type
                    </InputLabel>
                    <Select
                      labelId="workorder-type-label"
                      name="workorder_type_id"
                      value={formData.workorder_type_id}
                      onChange={handleFormChange}
                      required
                      label="Work Order Type"
                    >
                      {workOrderTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <FormControl fullWidth disabled={!subtypes.length}>
                      <InputLabel id="subtype-label">Subtype</InputLabel>
                      <Select
                        labelId="subtype-label"
                        name="workorder_subtype_id"
                        value={formData.workorder_subtype_id}
                        onChange={handleFormChange}
                        required
                        label="Subtype"
                      >
                        {subtypes.map((sub) => (
                          <MenuItem key={sub.id} value={sub.id}>
                            {sub.subtype_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth disabled={!locations.length}>
                      <InputLabel id="location-label">Location</InputLabel>
                      <Select
                        labelId="location-label"
                        name="location_id"
                        value={formData.location_id}
                        onChange={handleFormChange}
                        required
                        label="Location"
                      >
                        {locations.map((loc) => (
                          <MenuItem key={loc.id} value={loc.id}>
                            {loc.location_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    disabled
                  />

                  <TextField
                    fullWidth
                    label="Details"
                    name="details"
                    value={formData.details}
                    onChange={handleFormChange}
                    required
                    multiline
                    rows={4}
                  />

                  <TextField
                    fullWidth
                    label="Member ID"
                    name="property_id"
                    value={propertyId || ""}
                    disabled
                    InputProps={{
                      readOnly: true,
                      style: {
                        backgroundColor: "#f8fafc",
                        color: "#555",
                      },
                    }}
                  />
                </Stack>
              </Paper>

              <Paper elevation={0} sx={sectionCardSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <AttachFileOutlinedIcon sx={{ color: "#2f63ad" }} />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: "#223142" }}
                  >
                    Attach Files
                  </Typography>
                </Box>

                <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
                  Upload images, PDFs, or Word documents to support this request.
                </Typography>

                <Box
                  {...getRootProps()}
                  sx={{
                    p: 3,
                    border: "2px dashed #90caf9",
                    borderRadius: 2,
                    textAlign: "center",
                    cursor: "pointer",
                    mb: 2,
                    backgroundColor: isDragActive ? "#e3f2fd" : "#fafafa",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#f5faff",
                      borderColor: "#1976d2",
                    },
                  }}
                >
                  <input {...getInputProps()} />
                  <CloudUploadIcon
                    sx={{ fontSize: 40, color: "#1976d2", mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {isDragActive
                      ? "Drop files here..."
                      : "Drag & drop multiple files here, or click to select"}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ mt: 0.5 }}
                  >
                    Accepted: Images, PDFs, Word docs
                  </Typography>
                </Box>

                {selectedFiles.length > 0 && (
                  <Box
                    sx={{
                      border: "1px solid #eee",
                      borderRadius: 2,
                      p: 1,
                      maxHeight: 170,
                      overflowY: "auto",
                      backgroundColor: "#fff",
                    }}
                  >
                    {selectedFiles.map((file, idx) => (
                      <Box
                        key={`${file.name}-${file.size}-${file.lastModified}`}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          borderBottom:
                            idx !== selectedFiles.length - 1
                              ? "1px solid #f0f0f0"
                              : "none",
                          py: 0.75,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: "80%",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            color: "#223142",
                          }}
                        >
                          {file.name}
                        </Typography>

                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeFile(idx)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2.5,
              display: "flex",
              justifyContent: "space-between",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Button
              onClick={handleClose}
              disabled={isSubmitting}
              variant="outlined"
              sx={secondaryButtonSx}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? <CircularProgress size={18} color="inherit" /> : null
              }
              sx={primaryButtonSx}
            >
              {isSubmitting ? "Submitting..." : "Submit Work Order"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "18px",
            border: "1px solid rgba(47,99,173,0.08)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
            minWidth: 360,
            backgroundColor: "rgba(255,255,255,0.98)",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ height: 5, backgroundColor: "#2f63ad" }} />

        <DialogTitle
          sx={{
            textAlign: "center",
            pt: 3,
            pb: 1,
          }}
        >
          <Box
            sx={{
              width: 58,
              height: 58,
              mx: "auto",
              mb: 1.5,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(46,125,50,0.1)",
            }}
          >
            <CheckCircleOutlineIcon sx={{ color: "success.main", fontSize: 32 }} />
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#223142",
            }}
          >
            Work Order Created
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ textAlign: "center", pt: 0, pb: 1 }}>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            The work order has been successfully created.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 3, pt: 1 }}>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              handleClose();
              setTimeout(() => {
                setIsReloading(true);
                setTimeout(() => {
                  window.location.reload();
                }, 1200);
              }, 100);
            }}
            variant="contained"
            sx={primaryButtonSx}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {isReloading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(255,255,255,0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CircularProgress
            size={48}
            thickness={4}
            sx={{ color: "primary.main" }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RefreshIcon sx={{ color: "#5f6b7a" }} />
            <Typography variant="h6" sx={{ color: "#333", fontWeight: 500 }}>
              Refreshing your dashboard…
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default AddWorkOrder;